import React, { useEffect, useState } from 'react';
import url from './url';
import './BusinessAccountLedger.css';

const todayString = () => {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
};

const money = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const amount = Number(value);
    return Number.isFinite(amount)
        ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount)
        : '—';
};

const displayPercent = (value) => (
    value === null || value === undefined || value === '' ? 'Not recorded' : `${value}%`
);

const displayDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? '—'
        : date.toLocaleDateString('en-AU', {
            timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric',
        });
};

function BusinessAccountLedger({ accountId }) {
    const [period, setPeriod] = useState('all');
    const [anchorDate, setAnchorDate] = useState(todayString);
    const [page, setPage] = useState(1);
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [retryKey, setRetryKey] = useState(0);

    useEffect(() => {
        if (!accountId) return undefined;
        const controller = new AbortController();
        const params = new URLSearchParams({
            period,
            date: anchorDate,
            page: String(page),
            limit: '20',
        });
        const loadLedger = async () => {
            setLoading(true);
            setError('');
            setLedger(null);
            try {
                const response = await fetch(
                    `${url}/api/admin/business-accounts/${accountId}/ledger?${params.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                        signal: controller.signal,
                    }
                );
                const result = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(result.error || 'Unable to load this account ledger.');
                if (!controller.signal.aborted) setLedger(result);
            } catch (requestError) {
                if (requestError.name !== 'AbortError' && !controller.signal.aborted) {
                    setError(requestError.message || 'Unable to load this account ledger.');
                    setLedger(null);
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        loadLedger();
        return () => controller.abort();
    }, [accountId, period, anchorDate, page, retryKey]);

    const changePeriod = (event) => {
        setPeriod(event.target.value);
        setPage(1);
    };

    const changeDate = (event) => {
        setAnchorDate(event.target.value);
        setPage(1);
    };

    const bookings = ledger?.bookings || [];
    const summary = ledger?.summary || {};
    const pagination = ledger?.pagination || {};
    const totalPages = Math.max(1, Number(pagination.totalPages) || 1);

    return (
        <section className="business-ledger" aria-labelledby="business-ledger-title">
            <div className="business-ledger-heading">
                <div>
                    <span className="business-ledger-kicker">FINANCIAL DETAIL</span>
                    <h3 id="business-ledger-title">Commission ledger</h3>
                    <p>Historical booking values only. Rates are never inferred from current settings.</p>
                </div>
                <div className="business-ledger-total" aria-live="polite">
                    <span>Total commission</span>
                    <strong>{loading && !ledger ? '—' : money(summary.totalCommission)}</strong>
                    <small>{summary.eligibleBookings ?? '—'} eligible bookings</small>
                </div>
            </div>

            <div className="business-ledger-controls">
                <label>
                    <span>Period</span>
                    <select aria-label="Ledger period" value={period} onChange={changePeriod}>
                        <option value="all">All time</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                    </select>
                </label>
                {period !== 'all' && (
                    <label>
                        <span>{period === 'week' ? 'Week containing' : 'Month containing'}</span>
                        <input aria-label="Anchor date" type="date" value={anchorDate} onChange={changeDate} />
                    </label>
                )}
                <div className="business-ledger-period-label" aria-live="polite">
                    {ledger?.period?.label || (period === 'all' ? 'All recorded history' : 'Choose an anchor date')}
                    {ledger?.period?.timeZone && <span> · {ledger.period.timeZone}</span>}
                </div>
            </div>

            <div className="business-ledger-summary" aria-label="Ledger summary">
                <div><span>Bookings</span><strong>{loading && !ledger ? '—' : summary.totalBookings ?? 0}</strong></div>
                <div><span>Commission eligible</span><strong>{loading && !ledger ? '—' : summary.eligibleBookings ?? 0}</strong></div>
                <div><span>Unavailable history</span><strong>{loading && !ledger ? '—' : summary.unavailableBookings ?? 0}</strong></div>
            </div>

            {error ? (
                <div className="business-ledger-message business-ledger-error" role="alert">
                    <div><strong>Ledger unavailable</strong><p>{error}</p></div>
                    <button type="button" onClick={() => setRetryKey((key) => key + 1)}>Retry</button>
                </div>
            ) : loading ? (
                <div className="business-ledger-loading" role="status" aria-label="Loading ledger">
                    <span /><span /><span />
                </div>
            ) : bookings.length === 0 ? (
                <div className="business-ledger-empty">
                    <strong>No bookings in this period</strong>
                    <p>There are no ledger entries for the selected range.</p>
                </div>
            ) : (
                <>
                    <div className="business-ledger-table-wrap">
                        <table className="business-ledger-table">
                            <thead>
                                <tr>
                                    <th scope="col">Booking</th>
                                    <th scope="col">Route / courier</th>
                                    <th scope="col">Created</th>
                                    <th scope="col">Booking status</th>
                                    <th scope="col">Payment</th>
                                    <th scope="col">Price</th>
                                    <th scope="col">Admin markup</th>
                                    <th scope="col">Business markup</th>
                                    <th scope="col">Base after admin</th>
                                    <th scope="col">Shipping price</th>
                                    <th scope="col">Commission</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookings.map((booking) => {
                                    const commissionStatus = (booking.Commission_Status || 'unavailable').toLowerCase();
                                    return (
                                        <tr key={booking.Booking_ID}>
                                            <td data-label="Booking">
                                                <strong className="business-ledger-reference">{booking.OWCNumber || `#${booking.Booking_ID}`}</strong>
                                                <span className="business-ledger-subline">#{booking.Booking_ID}</span>
                                            </td>
                                            <td data-label="Route / courier">
                                                <span>{booking.From_City && booking.To_City ? `${booking.From_City} → ${booking.To_City}` : '—'}</span>
                                                <span className="business-ledger-subline">{booking.Courier || 'Courier not recorded'}</span>
                                            </td>
                                            <td data-label="Created">{displayDate(booking.Created_At)}</td>
                                            <td data-label="Booking status"><span className="business-ledger-status">{booking.Status || '—'}</span></td>
                                            <td data-label="Payment">{booking.Payment_Status || '—'}</td>
                                            <td data-label="Price">{money(booking.Price)}</td>
                                            <td data-label="Admin markup">{displayPercent(booking.Admin_Markup)}</td>
                                            <td data-label="Business markup">{displayPercent(booking.Business_Markup)}</td>
                                            <td data-label="Base after admin">{money(booking.Admin_Price)}</td>
                                            <td data-label="Shipping price">{money(booking.Shipping_Price)}</td>
                                            <td data-label="Commission">
                                                <strong>{money(booking.Commission)}</strong>
                                                <span className={`business-ledger-commission business-ledger-commission-${commissionStatus}`}>
                                                    {commissionStatus === 'earned' ? 'Earned' : commissionStatus === 'excluded' ? 'Excluded' : 'Unavailable'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <nav className="business-ledger-pagination" aria-label="Ledger pages">
                        <span>
                            Page {pagination.currentPage || page} of {pagination.totalPages || 1}
                            {pagination.total !== undefined && ` · ${pagination.total} bookings`}
                        </span>
                        <div>
                            <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1 || loading}>Previous</button>
                            <button type="button" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page >= totalPages || loading}>Next</button>
                        </div>
                    </nav>
                </>
            )}

            <p className="business-ledger-note">
                Commission is shipping price minus the base after admin markup; freight tax is included and insurance is excluded.
                Totals include paid bookings that are confirmed or fulfilled; canceled and refunded bookings are excluded.
                Historical rates are shown only when recorded.
            </p>
        </section>
    );
}

export default BusinessAccountLedger;
